// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import type { IlliquidAsset } from '@shared/data-access/types/engine-types';
import { TagObjectType } from '@shared/data-access/interfaces/redux/tags-management-state-types';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectAccountCurrency } from '@shared/data-access/slices/user';
import { useIlliquidAssets } from '../../hooks/use-illiquid-assets';
import {
  useObjectTagManagement,
  useTagManagementData,
} from '@shared/tag-management';
import { type KpiBoxesProps, useConfirmPopup } from '@shared/ui-kit/components';
import { formatToMoney } from '@shared/ui-kit/utils';
import { useSnackbar } from '@shared/ui-kit/providers/snackbar-provider';
import { isNullOrUndefined, isUndefined } from '@shared/utils';
import { filterIlliquidAssetsByTag } from '../../utils/filter-illiquid-assets-by-tag';
import { illiquidsApi } from '@shared/data-access/api/engine/illiquids-api';
import { type LoaderStatus } from '../loader';
import { type IlliquidAssetFormData } from '../../types/illiquid-asset-form';
import { buildIlliquidAssetCreateBody } from '../../utils/build-illiquid-asset-create-body';
import type { IlliquidAssetEvent } from '@shared/data-access/types/engine-types';

export interface UseIliquidAssetsProps {
  readonly clientId: number;
}

export const useIliquidAssets = ({ clientId }: UseIliquidAssetsProps) => {
  const { t } = useTranslation();

  const accountCurrency = useSelector(selectAccountCurrency);

  const {
    illiquidAssets: allAssets,
    illiquidAssetsQuery,
    listIlliquidParams,
  } = useIlliquidAssets({
    clientId,
    currency: accountCurrency,
  });

  const [createIlliquidAsset, createIlliquidAssetState] =
    illiquidsApi.useCreateIlliquidAssetMutation();

  const [deleteIlliquidAsset] = illiquidsApi.useDeleteIlliquidAssetMutation();

  const showConfirm = useConfirmPopup();

  const { enqueueSnackbar } = useSnackbar();

  const [addDrawerOpen, setAddDrawerOpen] = useState(false);

  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);

  const [selectedAsset, setSelectedAsset] = useState<IlliquidAsset | undefined>();

  const { tags, tagsByObject } = useTagManagementData();

  const [selectedTag, setSelectedTag] = useState<string | undefined>('');

  const tagsOptions = useMemo(
    () =>
      [
        { label: t('aggregator.all-accounts', { ns: 'aggregator' }), value: '' },
      ].concat(tags.map(tag => ({ label: tag.name, value: String(tag.tag_id) }))),
    [tags, t],
  );

  const filteredAssets = useMemo(
    () =>
      filterIlliquidAssetsByTag({
        assets: allAssets,
        selectedTag,
        tagsByObject,
      }),
    [allAssets, selectedTag, tagsByObject],
  );

  const detailAsset = useMemo(
    () =>
      isNullOrUndefined(selectedAsset)
        ? undefined
        : allAssets.find(asset => asset.id === selectedAsset.id) ?? selectedAsset,
    [allAssets, selectedAsset],
  );

  const kpis = useMemo<KpiBoxesProps[]>(
    () => [
      {
        text: t('aggregator.total-illiquid-assets', { ns: 'aggregator' }),
        value: formatToMoney(
          filteredAssets.reduce(
            (sum, row) => sum + (row.current_valuation ?? 0),
            0,
          ),
          accountCurrency,
        ),
        icon: 'Layers',
        color: 'main',
      },
      {
        text: t('aggregator.number-of-assets', { ns: 'aggregator' }),
        value: filteredAssets.length,
        icon: 'Package',
        color: 'secondary',
      },
      {
        text: t('aggregator.linked-accounts', { ns: 'aggregator' }),
        value: new Set(filteredAssets.flatMap(asset => asset.ibans ?? [])).size,
        icon: 'Link',
        color: 'main',
      },
    ],
    [filteredAssets, accountCurrency, t],
  );

  const status = useMemo<LoaderStatus>(() => {
    if (illiquidAssetsQuery.isLoading) {
      return 'pending';
    }

    if (illiquidAssetsQuery.isError) {
      return 'rejected';
    }

    // Keep content stable during background refetches (no LinearProgress flash).
    return 'fulfilled';
  }, [illiquidAssetsQuery.isError, illiquidAssetsQuery.isLoading]);

  const onAddDrawerOpen = useCallback(() => {
    setAddDrawerOpen(true);
  }, []);

  const onRefresh = useCallback(() => {
    void illiquidAssetsQuery.refetch();
  }, [illiquidAssetsQuery]);

  const onAddDrawerClose = useCallback(() => {
    setAddDrawerOpen(false);
  }, []);

  const onDetailDrawerCloseBase = useCallback(() => {
    setDetailDrawerOpen(false);

    setSelectedAsset(undefined);
  }, []);

  const objectTagManagement = useObjectTagManagement({
    objectType: TagObjectType.IlliquidAsset,
    objectId: String(selectedAsset?.id ?? '_'),
    onClose: onDetailDrawerCloseBase,
  });

  const onDetailDrawerOpen = useCallback((asset: IlliquidAsset) => {
    setSelectedAsset(asset);

    setDetailDrawerOpen(true);
  }, []);

  const onDetailDrawerShow = useCallback(() => {
    setDetailDrawerOpen(true);
  }, []);

  const onAddDrawerSubmit = useCallback(
    async (data: IlliquidAssetFormData, draftEvents: IlliquidAssetEvent[] = []) => {
      try {
        const body = buildIlliquidAssetCreateBody(data, draftEvents);

        await createIlliquidAsset({
          body,
          ...listIlliquidParams,
        }).unwrap();

        enqueueSnackbar(
          t('illiquid-asset-drawer.messages.asset-saved', { ns: 'components' }),
          { variant: 'success' },
        );

        onAddDrawerClose();
      } catch {
        enqueueSnackbar(
          t('illiquid-asset-drawer.messages.asset-save-error', {
            ns: 'components',
          }),
          { variant: 'error' },
        );
      }
    },
    [createIlliquidAsset, enqueueSnackbar, listIlliquidParams, onAddDrawerClose, t],
  );

  const onDetailDrawerDelete = useCallback(() => {
    if (isUndefined(selectedAsset)) {
      return;
    }

    const assetId = selectedAsset.id;

    const assetName = selectedAsset.name;

    showConfirm({
      title: t('illiquid-asset-drawer.delete-confirm-title', {
        ns: 'components',
      }),
      message: t('illiquid-asset-drawer.delete-confirm-message', {
        ns: 'components',
        assetName,
      }),
      confirmText: t('illiquid-asset-drawer.delete-asset', { ns: 'components' }),
      onConfirm: async () => {
        await deleteIlliquidAsset({
          asset_id: assetId,
          client_id: listIlliquidParams.client_id,
        }).unwrap();

        onDetailDrawerCloseBase();
      },
    });
  }, [
    deleteIlliquidAsset,
    listIlliquidParams,
    onDetailDrawerCloseBase,
    selectedAsset,
    showConfirm,
    t,
  ]);

  return {
    t,
    allAssetsCount: allAssets.length,
    assets: filteredAssets,
    kpis,
    status,
    isRefreshing: illiquidAssetsQuery.isFetching,
    addDrawerOpen,
    onAddDrawerOpen,
    onAddDrawerClose,
    onRefresh,
    detailDrawerOpen,
    selectedAsset: detailAsset,
    onDetailDrawerOpen,
    onDetailDrawerShow,
    onDetailDrawerClose: objectTagManagement.onCloseWithSync,
    listIlliquidParams,
    tagsOptions,
    selectedTag,
    setSelectedTag,
    tags: objectTagManagement.tags,
    initialAssignedTags: objectTagManagement.initialAssignedTags,
    onChangeTagIds: objectTagManagement.onChangeTagIds,
    onCreateTag: objectTagManagement.onCreateTag,
    onAddDrawerSubmit,
    isCreatingAsset: createIlliquidAssetState.isLoading,
    onDetailDrawerDelete,
    currency: accountCurrency,
  };
};
